/**
 * Helpers for loading/saving HTML in the shared CKEditor wrapper.
 *
 * Legacy CMS content is often pretty-printed (newlines/tabs between tags).
 * Browsers collapse that whitespace on display, but a naive `\n` → `<br>`
 * transform turns it into real breaks and corrupts spacing on save.
 */

const HTML_TAG_PATTERN = /<\/?[a-z][\s\S]*>/i;

/** Leading/trailing empty paragraphs often left by the CMS editor. */
const LEADING_EMPTY_PARAGRAPHS =
  /^(?:\s*<p[^>]*>\s*(?:&nbsp;|\u00a0|\s|<br\s*\/?>)*\s*<\/p>)+/i;
const TRAILING_EMPTY_PARAGRAPHS =
  /(?:\s*<p[^>]*>\s*(?:&nbsp;|\u00a0|\s|<br\s*\/?>)*\s*<\/p>)+\s*$/i;

/** True when the string already contains HTML markup (not plain text). */
export function containsHtmlTags(value: string): boolean {
  return HTML_TAG_PATTERN.test(value);
}

/**
 * Collapse pretty-print whitespace between tags (newlines/CR + surrounding
 * tabs/spaces). Does NOT remove a plain space between inline tags, e.g.
 * `</strong> <em>`, which is a real word separator in the rendered text.
 *
 * Intentional breaks stay as real elements (`<br>`, `<p>`, lists, etc.).
 */
export function collapseInterTagWhitespace(html: string): string {
  return html.replace(/>[ \t]*[\r\n]+[ \t\r\n]*</g, '><');
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Convert plain-text legacy content into paragraph HTML so CKEditor
 * preserves line breaks without a blind newline → `<br>` pass.
 * Matches SafeHTMLDisplay's plain-text path (one visual block per line).
 */
export function plainTextToEditorHtml(text: string): string {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');

  return lines
    .map((line) => line.trimEnd())
    .filter((line, index, arr) => {
      // Keep internal blank lines as empty paragraphs only when they
      // separate non-empty content; drop leading/trailing blanks.
      if (line.trim() !== '') return true;
      const hasContentBefore = arr.slice(0, index).some((l) => l.trim() !== '');
      const hasContentAfter = arr.slice(index + 1).some((l) => l.trim() !== '');
      return hasContentBefore && hasContentAfter;
    })
    .map((line) => (line.trim() === '' ? '<p>&nbsp;</p>' : `<p>${escapeHtml(line)}</p>`))
    .join('');
}

/**
 * Prepare DB/API content for CKEditor's `data` prop.
 * - HTML: strip insignificant inter-tag whitespace / leftover newlines
 * - Plain text: wrap lines in `<p>` so breaks remain visible
 */
export function prepareHtmlForEditor(value: string | null | undefined): string {
  if (value == null || value === '') {
    return '';
  }

  if (containsHtmlTags(value)) {
    return collapseInterTagWhitespace(value)
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      // Any remaining newlines are leftover pretty-print or mid-tag noise;
      // do not turn them into <br>.
      .replace(/\n/g, '')
      .trim();
  }

  return plainTextToEditorHtml(value);
}

/**
 * Normalize HTML from `editor.getData()` before writing back to Formik/API.
 * Keeps compact, stable markup without changing intentional structure.
 */
export function normalizeHtmlOnSave(html: string | null | undefined): string {
  if (html == null || html === '') {
    return '';
  }

  let result = collapseInterTagWhitespace(html)
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n/g, '')
    .trim();

  // Drop leading/trailing empty paragraphs often left by the editor
  result = result.replace(LEADING_EMPTY_PARAGRAPHS, '').replace(TRAILING_EMPTY_PARAGRAPHS, '');

  return result.trim();
}
